import { z } from 'zod';
import {
  appKeySchema,
  decimalString,
  iconKeySchema,
  isoDate,
  isoDateTime,
  localeSchema,
  tableOwnerSchema,
} from './common';
import { CALORIE_DAY_VIEWS } from './inputs';

/**
 * Response shapes, described as they appear *on the wire* — dates are ISO
 * strings, not `Date`, because that is what survives JSON. Handlers map their
 * Drizzle rows onto these; the row types are not the DTO types.
 */

/** Note what is absent: `passwordHash`. These four fields are the public ones. */
export const userSchema = z.object({
  id: z.string().uuid(),
  login: z.string(),
  locale: localeSchema,
  createdAt: isoDateTime,
});
export type UserDto = z.infer<typeof userSchema>;

export const userWithAccessSchema = z.object({
  user: userSchema,
  apps: z.array(appKeySchema),
});
export type UserWithAccessDto = z.infer<typeof userWithAccessSchema>;

export const sessionUserSchema = z.object({
  user: userSchema,
  apps: z.array(appKeySchema),
  version: z.string(),
});
export type SessionUserDto = z.infer<typeof sessionUserSchema>;

export const adminSummarySchema = z.object({
  userCount: z.number().int(),
  appCount: z.number().int(),
  /** The server's root filesystem in bytes; used is total minus free. */
  disk: z.object({
    totalBytes: z.number(),
    freeBytes: z.number(),
  }),
  /**
   * Current sizes in bytes and exact row counts. `otherBytes` is what
   * no owner prefix accounts for — catalog, migration bookkeeping, strays.
   */
  database: z.object({
    totalBytes: z.number(),
    owners: z.array(
      z.object({
        owner: tableOwnerSchema,
        bytes: z.number(),
        rows: z.number().int(),
      }),
    ),
    otherBytes: z.number(),
  }),
});
export type AdminSummaryDto = z.infer<typeof adminSummarySchema>;

export const walletSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  balance: decimalString,
  createdAt: isoDateTime,
});
export type WalletDto = z.infer<typeof walletSchema>;

export const goalSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  targetAmount: decimalString,
  targetDate: isoDate,
  createdAt: isoDateTime,
});
export type GoalDto = z.infer<typeof goalSchema>;

/** One row of the overview waterfall — see `finances/summary.ts`. */
export const goalAllocationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  targetAmount: decimalString,
  targetDate: isoDate,
  allocated: decimalString,
  shortfall: decimalString,
  fullyFunded: z.boolean(),
  monthsRemaining: z.number().int(),
});
export type GoalAllocationDto = z.infer<typeof goalAllocationSchema>;

export const financesSummarySchema = z.object({
  totalBalance: decimalString,
  totalTargets: decimalString,
  unallocated: decimalString,
  shortfall: decimalString,
  underfunded: z.boolean(),
  goals: z.array(goalAllocationSchema),
});
export type FinancesSummaryDto = z.infer<typeof financesSummarySchema>;

export const usefulLinkSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  url: z.string(),
  iconKey: iconKeySchema,
  sortOrder: z.number().int(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type UsefulLinkDto = z.infer<typeof usefulLinkSchema>;

export const brainNoteSchema = z.object({
  id: z.string().uuid(),
  content: z.string(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type BrainNoteDto = z.infer<typeof brainNoteSchema>;

/**
 * An answer plus the notes it was drawn from, so the UI can show its work and
 * the reader can judge whether the right fact was found. `answer` is `null`
 * when no note answers the question — `sources` is then empty, and the UI says
 * so in its own language. The embedding itself never goes on the wire — 1536
 * floats per note, for nothing.
 */
export const brainAnswerSchema = z.object({
  answer: z.string().nullable(),
  sources: z.array(brainNoteSchema),
});
export type BrainAnswerDto = z.infer<typeof brainAnswerSchema>;

/** A section of the day the user set up — "Śniadanie", "Obiad"… */
export const calorieMealSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});
export type CalorieMealDto = z.infer<typeof calorieMealSchema>;

/**
 * One product eaten on one day — a copy of what was typed, not a reference to
 * the catalog. Either number may still be missing; `kcal` is then `null`.
 * `kcal` is derived on the server — see `entryKcal` — and never stored.
 */
export const calorieEntrySchema = z.object({
  id: z.string().uuid(),
  date: isoDate,
  /** `null` is "Bez sekcji". */
  mealId: z.string().uuid().nullable(),
  name: z.string(),
  kcalPer100g: z.number().int().nullable(),
  /** One piece; the entry counts `quantity` of them. */
  grams: z.number().int().nullable(),
  quantity: z.number(),
  kcal: z.number().int().nullable(),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type CalorieEntryDto = z.infer<typeof calorieEntrySchema>;

/**
 * A meal's entries on one day, or the section-less ones when `meal` is `null`.
 * `kcal` only counts entries that have one; `incompleteCount` is the rest.
 */
export const calorieDaySectionSchema = z.object({
  meal: calorieMealSchema.nullable(),
  kcal: z.number().int(),
  incompleteCount: z.number().int(),
  entries: z.array(calorieEntrySchema),
});
export type CalorieDaySectionDto = z.infer<typeof calorieDaySectionSchema>;

/**
 * Every meal in the user's order — empty ones too, so each has somewhere to
 * add to — then "Bez sekcji" last.
 */
export const calorieDaySchema = z.object({
  date: isoDate,
  kcal: z.number().int(),
  incompleteCount: z.number().int(),
  sections: z.array(calorieDaySectionSchema),
});
export type CalorieDayDto = z.infer<typeof calorieDaySchema>;

/** Always present — a user who never saved one gets the default limit. */
export const calorieSettingsSchema = z.object({
  dailyLimitKcal: z.number().int(),
  dayView: z.enum(CALORIE_DAY_VIEWS),
  /** `null` until the user sets a target. */
  targetWeightKg: z.number().nullable(),
});
export type CalorieSettingsDto = z.infer<typeof calorieSettingsSchema>;

/** The user's weight on one day — there is at most one per day. */
export const calorieWeightSchema = z.object({
  id: z.string().uuid(),
  date: isoDate,
  weightKg: z.number(),
});
export type CalorieWeightDto = z.infer<typeof calorieWeightSchema>;

/**
 * A catalog product: what a new entry's form fills in when picked. Names are
 * unique per user together with dish names, ignoring case.
 */
export const calorieProductSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  kcalPer100g: z.number().int().nullable(),
  /** The usual piece. */
  grams: z.number().int().nullable(),
});
export type CalorieProductDto = z.infer<typeof calorieProductSchema>;

/** One ingredient of a dish — a copy, like an entry; either number may be missing. */
export const calorieDishIngredientSchema = z.object({
  name: z.string(),
  kcalPer100g: z.number().int().nullable(),
  /** The amount used in the dish. */
  grams: z.number().int().nullable(),
});
export type CalorieDishIngredientDto = z.infer<
  typeof calorieDishIngredientSchema
>;

/**
 * A catalog dish, its ingredients in the user's order. `grams` and
 * `kcalPer100g` are derived on the server — see `dishTotals` — and never
 * stored; `incomplete` while any ingredient lacks a number, or there is none.
 * Names are unique per user together with product names, ignoring case.
 */
export const calorieDishSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  ingredients: z.array(calorieDishIngredientSchema),
  grams: z.number().int().nullable(),
  kcalPer100g: z.number().int().nullable(),
  incomplete: z.boolean(),
});
export type CalorieDishDto = z.infer<typeof calorieDishSchema>;

/**
 * A recipe, whole. The list returns these too, not a summary: search runs in
 * the browser over the name, the ingredient lines and the tags.
 */
export const recipeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  servings: z.number().int().nullable(),
  minutes: z.number().int().nullable(),
  ingredients: z.array(z.string()),
  steps: z.array(z.string()),
  tags: z.array(z.string()),
  notes: z.string().nullable(),
});
export type RecipeDto = z.infer<typeof recipeSchema>;

export const checklistItemSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  checked: z.boolean(),
});
export type ChecklistItemDto = z.infer<typeof checklistItemSchema>;

/** A group of the user's checklists — "Wyjazdy", "Dom"… */
export const checklistGroupSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
});
export type ChecklistGroupDto = z.infer<typeof checklistGroupSchema>;

/**
 * A checklist, whole. A standard list carries its items in order; a combined
 * list carries only its children's ids, in the user's order, which the client
 * resolves from the same list response, so no list's items travel twice.
 */
export const checklistSchema = z.discriminatedUnion('kind', [
  z.object({
    id: z.string().uuid(),
    name: z.string(),
    groupId: z.string().uuid(),
    kind: z.literal('standard'),
    items: z.array(checklistItemSchema),
  }),
  z.object({
    id: z.string().uuid(),
    name: z.string(),
    groupId: z.string().uuid(),
    kind: z.literal('combined'),
    childIds: z.array(z.string().uuid()),
  }),
]);
export type ChecklistDto = z.infer<typeof checklistSchema>;
